import { useState, useEffect } from 'react';

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if running in iOS Safari (PWA not installed)
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);

    if (isIOS && !isInStandaloneMode) {
      setIsInstallable(true); // Show manual instructions for iOS
      return;
    }

    // Listen for beforeinstallprompt event (Chrome/Edge on Android/Desktop/Windows)
    const handleBeforeInstallPrompt = (e) => {
      // console.log('[PWA] beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      // console.log('[PWA] App installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      // console.log('[PWA] No deferred prompt available');
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;
      // console.log('[PWA] User choice:', outcome);

      // Clear the deferred prompt
      setDeferredPrompt(null);

      if (outcome === 'accepted') {
        setIsInstallable(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('[PWA] Error prompting install:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    setIsInstallable(false);
    // Store dismissal in localStorage to not show again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Check if user dismissed recently (within 7 days)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDaysInMs) {
        setIsInstallable(false);
      }
    }
  }, []);

  return {
    isInstallable: isInstallable && !isInstalled,
    isInstalled,
    promptInstall,
    dismissPrompt,
    isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent)
  };
}
